import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { clsx } from "clsx"
import { Link } from "react-router-dom"

import { USER_KEYS } from "~/api/cache"
import type {
  BatteryArbitrageDto,
  BatteryArbitrageResultDto,
  BatteryArbitrageSettingsDto,
} from "~/api/dto/battery-arbitrage.dto"
import useBatteryProposalArbitrage, {
  savingsAreShown,
} from "~/api/hooks/useBatteryProposalArbitrage"
import useRetryBatteryProposalArbitrage from "~/api/hooks/useRetryBatteryProposalArbitrage"
import useSetBatterySavingsVisibility from "~/api/hooks/useSetBatterySavingsVisibility"
import useUpdateBatteryProposalArbitrageSettings from "~/api/hooks/useUpdateBatteryProposalArbitrageSettings"
import { userService } from "~/api/services/user.service"
import {
  Drawer,
  SelectField,
  TextField,
  ToggleField,
} from "~/components/ui-components/v2"

import { BatterySavingsReportAction } from "./BatterySavingsReportAction"
import {
  arbitrageAssumptions,
  arbitrageBasisQualifier,
  arbitrageBillLabel,
  arbitrageScopeExplanation,
} from "../arbitrage-assumptions"
import { formatUsd } from "../view-helpers"
import {
  batteryArbitrageStatusView,
  savingsVisibilityView,
  type BatteryArbitrageTone,
} from "./battery-arbitrage-status"
import { BatterySavingsInputControl } from "./BatterySavingsInputControl"
import { BatterySavingsRateSummary } from "./BatterySavingsRateSummary"
import { batterySetupStepPath, SETUP_STEP_LABELS } from "./setup-steps"

export const BACKUP_RESERVE_ERROR = `Enter a backup reserve from 0 to 100.`
export const ESCALATION_ERROR = `Enter an annual rate change of -100 or more.`
/** Fixed by product decision; the seller cannot change it, so it reads as a fact. */
export const DEGRADATION_NOTE = `2% a year, fixed`

const TONE_PANEL: Record<BatteryArbitrageTone, string> = {
  busy: `border-bp-border bg-bp-elevated-2`,
  info: `border-bp-border bg-bp-elevated-2`,
  problem: `border-bp-danger/40 bg-bp-danger/10`,
  ready: `border-bp-accent/40 bg-bp-accent-soft`,
}

const TONE_HEADLINE: Record<BatteryArbitrageTone, string> = {
  busy: `text-foreground`,
  info: `text-foreground`,
  problem: `text-danger`,
  ready: `text-bp-accent`,
}

type ExistingSolarOption = {
  readonly value: boolean | null
  readonly label: string
}

const EXISTING_SOLAR_OPTIONS: readonly ExistingSolarOption[] = [
  { value: null, label: `Not answered` },
  { value: false, label: `No existing solar` },
  { value: true, label: `Has existing solar` },
]

const percentDraft = (value: number): string => String(value)

/** A plain decimal percentage. `Number` alone would also take `0x64` and `1e3`. */
const PERCENT_PATTERN = /^-?\d+(\.\d+)?$/

const parsePercent = (
  draft: string,
  { min, max }: { min: number; max?: number },
): number | null => {
  const trimmed = draft.trim()
  if (!PERCENT_PATTERN.test(trimmed)) {
    return null
  }
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed) || parsed < min) {
    return null
  }
  return max != null && parsed > max ? null : parsed
}

const AssumptionRow = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): JSX.Element => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="text-[length:var(--step--2)] text-muted">{label}</span>
    <span className="text-right text-[length:var(--step--2)] text-foreground-soft">
      {children}
    </span>
  </div>
)

const ReadyResult = ({
  result,
}: {
  result: BatteryArbitrageResultDto
}): JSX.Element => {
  const bills: Array<[string, string]> =
    result.beforeAnnualBillUsd != null && result.afterAnnualBillUsd != null
      ? [
          [
            `${arbitrageBillLabel(result)} without battery`,
            formatUsd(result.beforeAnnualBillUsd),
          ],
          [
            `${arbitrageBillLabel(result)} with battery`,
            formatUsd(result.afterAnnualBillUsd),
          ],
        ]
      : []
  return (
    <div className="mt-4 flex flex-col gap-4">
      <p className="text-sm text-muted">
        {arbitrageBasisQualifier(result)}. {arbitrageScopeExplanation(result)}
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        {(
          [
            ...bills,
            [`First-year savings`, formatUsd(result.annualSavingsUsd)],
          ] as const
        ).map(([label, value]) => (
          <div
            key={label}
            className="rounded-lg border border-bp-border bg-bp-elevated px-3 py-2.5"
          >
            <div className="text-[length:var(--step--2)] text-muted">
              {label}
            </div>
            <div className="mt-1 text-[length:var(--step-0)] font-semibold text-foreground tabular-nums">
              {value}
            </div>
          </div>
        ))}
      </div>
      {result.assumptions.limitations.length > 0 ? (
        <ul className="list-disc space-y-1 pl-5 text-[length:var(--step--2)] text-foreground-soft">
          {result.assumptions.limitations.map((limitation) => (
            <li key={limitation}>{limitation}</li>
          ))}
        </ul>
      ) : null}
      {result.annualSavingsUsd <= 0 ? (
        <p
          role="status"
          className="text-[length:var(--step--2)] text-foreground-soft"
        >
          The saved usage and rate assumptions produce no reduction in modeled
          utility costs. The figure above retains the calculation&rsquo;s result.
        </p>
      ) : null}
      <div className="flex flex-col gap-1.5 border-t border-border pt-3">
        {arbitrageAssumptions(result.assumptions).map(({ label, value }) => (
          <AssumptionRow key={label} label={label}>
            {value}
          </AssumptionRow>
        ))}
      </div>
    </div>
  )
}

const StatusPanel = ({
  proposalId,
  arbitrage,
  isAdmin,
}: {
  proposalId: string
  arbitrage: BatteryArbitrageDto
  isAdmin: boolean
}): JSX.Element | null => {
  const view = batteryArbitrageStatusView(arbitrage)
  const retry = useRetryBatteryProposalArbitrage()
  const canRetry =
    view.actionLabel != null && savingsAreShown(arbitrage.visibility)
  const hasActions = view.fixStep != null || canRetry

  if (!isAdmin && !hasActions && !retry.isError) {
    return null
  }

  return (
    <section
      aria-label={
        isAdmin ? `Savings calculation status` : `Savings calculation actions`
      }
      data-testid={isAdmin ? `arbitrage-status` : undefined}
      data-status={arbitrage.status}
      className={
        isAdmin
          ? clsx(`rounded-lg border p-4`, TONE_PANEL[view.tone])
          : undefined
      }
    >
      {isAdmin ? (
        <>
          <h3
            className={clsx(
              `font-display text-[length:var(--step-0)] font-semibold`,
              TONE_HEADLINE[view.tone],
            )}
          >
            {view.headline}
          </h3>
          <p className="mt-1.5 text-[length:var(--step--2)] leading-snug text-foreground-soft">
            {view.detail}
          </p>

          {arbitrage.status === `ready` && arbitrage.result ? (
            <ReadyResult result={arbitrage.result} />
          ) : null}
        </>
      ) : null}

      {hasActions ? (
        <div className="mt-3 flex flex-wrap items-center gap-4">
          {view.fixStep != null ? (
            <Link
              to={batterySetupStepPath(proposalId, view.fixStep)}
              className="text-[length:var(--step--2)] font-medium text-foreground underline underline-offset-2 hover:text-foreground-soft"
            >
              Open {SETUP_STEP_LABELS[view.fixStep]}
            </Link>
          ) : null}
          {canRetry ? (
            <button
              type="button"
              disabled={retry.isLoading}
              onClick={() => {
                retry.mutate({ proposalId })
              }}
              className="rounded-md border border-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {retry.isLoading ? `Starting` : view.actionLabel}
            </button>
          ) : null}
        </div>
      ) : null}

      {retry.isError ? (
        <p
          role="alert"
          className="mt-3 text-[length:var(--step--2)] text-danger"
        >
          Unable to start another calculation. Try again in a moment.
        </p>
      ) : null}
    </section>
  )
}

const SettingsForm = ({
  proposalId,
  settings,

  onClose,
}: {
  proposalId: string
  settings: BatteryArbitrageSettingsDto

  onClose: () => void
}): JSX.Element => {
  const save = useUpdateBatteryProposalArbitrageSettings()
  const [reserveDraft, setReserveDraft] = useState(() =>
    percentDraft(settings.backupReservePct),
  )
  const [escalationDraft, setEscalationDraft] = useState(() =>
    percentDraft(settings.utilityRateEscalationPct),
  )
  const [existingSolar, setExistingSolar] = useState(settings.hasExistingSolar)
  // The drafts follow the server's answer, the way System Pricing syncs its
  // margin: state during render, never an Effect. This read polls while a run
  // is in flight, so the sync has to leave unsaved typing alone — otherwise a
  // poll three seconds into an edit takes the seller's number away mid-keystroke.
  const [synced, setSynced] = useState(settings)
  const untouched =
    reserveDraft === percentDraft(synced.backupReservePct) &&
    escalationDraft === percentDraft(synced.utilityRateEscalationPct) &&
    existingSolar === synced.hasExistingSolar
  if (synced !== settings && untouched) {
    setSynced(settings)
    setReserveDraft(percentDraft(settings.backupReservePct))
    setEscalationDraft(percentDraft(settings.utilityRateEscalationPct))
    setExistingSolar(settings.hasExistingSolar)
  }

  const reserve = parsePercent(reserveDraft, { min: 0, max: 100 })
  const escalation = parsePercent(escalationDraft, { min: -100 })
  const reserveError = reserve == null ? BACKUP_RESERVE_ERROR : null
  const escalationError = escalation == null ? ESCALATION_ERROR : null
  const changed = {
    ...(reserve !== settings.backupReservePct && reserve != null
      ? { backupReservePct: reserve }
      : {}),
    ...(escalation !== settings.utilityRateEscalationPct && escalation != null
      ? { utilityRateEscalationPct: escalation }
      : {}),
    ...(existingSolar !== settings.hasExistingSolar
      ? { hasExistingSolar: existingSolar }
      : {}),
  }
  const dirty = Object.keys(changed).length > 0
  const blocked = save.isLoading || reserve == null || escalation == null

  const submit = (): void => {
    if (blocked || !dirty) {
      return
    }
    // Only what this seller actually changed. The endpoint preserves omitted
    // fields, so a field someone else moved in the meantime is not reverted by
    // a save the seller believes touched one number.
    save.mutate({ proposalId, body: changed })
  }

  return (
    <section
      aria-label="Savings assumptions"
      className="mt-6 flex flex-col gap-5"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Backup reserve"
          description="Capacity held back for outages. 100% leaves nothing for bill savings."
          trailingAddon="%"
          inputMode="decimal"
          autoComplete="off"
          value={reserveDraft}
          error={reserveError}
          disabled={save.isLoading}
          onChange={(event) => {
            save.reset()
            setReserveDraft(event.target.value)
          }}
        />
        <TextField
          label="Annual utility-rate change"
          description="Applied to the ten-year projection. 0% assumes rates hold."
          trailingAddon="%"
          inputMode="decimal"
          autoComplete="off"
          value={escalationDraft}
          error={escalationError}
          disabled={save.isLoading}
          onChange={(event) => {
            save.reset()
            setEscalationDraft(event.target.value)
          }}
        />
      </div>

      <SelectField
        label="Existing solar on this home"
        description="Savings model grid purchases. Existing solar may require a generalized scenario when a complete utility bill cannot be established."
        // Anchored, not portalled, like every other select in these drawers.
        // The portalled list lands outside the drawer's stacking context and
        // the drawer surface then swallows the click on an option.
        portal={false}
        options={[...EXISTING_SOLAR_OPTIONS]}
        value={
          EXISTING_SOLAR_OPTIONS.find(
            (option) => option.value === existingSolar,
          ) ?? null
        }
        disabled={save.isLoading}
        getKey={(option) => String(option.value)}
        getLabel={(option) => option.label}
        onChange={(option) => {
          save.reset()
          setExistingSolar(option.value)
        }}
      />

      <div className="rounded-lg border border-border bg-bp-elevated-2 px-4 py-3">
        <AssumptionRow label="Battery benefit decline">
          {DEGRADATION_NOTE}
        </AssumptionRow>
      </div>

      <div className="flex flex-col gap-3 border-t border-border pt-5">
        {save.isError ? (
          <p role="alert" className="text-[length:var(--step--2)] text-danger">
            Unable to save these assumptions. Your changes are still here.
          </p>
        ) : null}
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-strong px-4 py-2 text-[length:var(--step--1)] font-medium text-foreground"
          >
            Close
          </button>
          <button
            type="button"
            disabled={blocked || !dirty}
            onClick={submit}
            className="rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isLoading ? `Saving` : `Save assumptions`}
          </button>
        </div>
      </div>
    </section>
  )
}

export const ArbitrageStep = ({
  proposalId,
  onClose,
}: {
  proposalId: string
  onClose: () => void
}): JSX.Element => {
  const arbitrageQuery = useBatteryProposalArbitrage(proposalId)
  const setVisibility = useSetBatterySavingsVisibility()
  const { data: profile } = useQuery({
    queryKey: USER_KEYS.profile(),
    queryFn: () => userService.getProfile(),
  })
  const isAdmin =
    profile?.permissions.some(
      (permission) => permission === `admin` || permission === `vision_admin`,
    ) ?? false

  // Whatever the last good read said. A failed *background* poll must not tear
  // the form down: the seller may be part-way through typing a reserve, and
  // unmounting takes those keystrokes with it.
  const arbitrage = arbitrageQuery.data

  return (
    <Drawer
      open
      theme="dark"
      onClose={onClose}
      title={SETUP_STEP_LABELS.arbitrage}
      widthClass="sm:max-w-2xl"
      contentClassName="flex min-h-0 flex-col"
    >
      {arbitrageQuery.isLoading ? (
        <p className="text-[length:var(--step--1)] text-muted">
          Loading savings settings
        </p>
      ) : null}

      {arbitrageQuery.isError ? (
        <div className="mb-4 flex flex-col gap-2">
          <p role="alert" className="text-[length:var(--step--2)] text-danger">
            {arbitrage == null
              ? `Unable to load savings settings`
              : `Unable to check the savings status just now. What is below is the last answer.`}
          </p>
          <button
            type="button"
            onClick={() => {
              void arbitrageQuery.refetch()
            }}
            className="self-start rounded-md border border-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-medium text-foreground"
          >
            Retry
          </button>
        </div>
      ) : null}

      {arbitrage != null ? (
        <>
          <ToggleField
            className="mb-4"
            label="Show on proposal"
            labelPosition="start"
            description={
              arbitrage.visibility === `hidden`
                ? `${savingsVisibilityView.hidden.headline}. ${savingsVisibilityView.hidden.detail}`
                : undefined
            }
            error={
              setVisibility.isError
                ? `Unable to change Show on proposal. Try again.`
                : undefined
            }
            checked={savingsAreShown(arbitrage.visibility)}
            disabled={setVisibility.isLoading}
            onChange={(shown) => {
              setVisibility.mutate({
                proposalId,
                visibility: shown ? `shown` : `hidden`,
              })
            }}
          />
          <BatterySavingsInputControl
            proposalId={proposalId}
            savingsInput={arbitrage.savingsInput}
          />
          <StatusPanel
            proposalId={proposalId}
            arbitrage={arbitrage}
            isAdmin={isAdmin}
          />
          {arbitrage.status === `ready` &&
          savingsAreShown(arbitrage.visibility) &&
          arbitrage.result ? (
            <div className="mb-4">
              <BatterySavingsRateSummary result={arbitrage.result} />
            </div>
          ) : null}
          <BatterySavingsReportAction proposalId={proposalId} />
          <SettingsForm
            proposalId={proposalId}
            settings={arbitrage.settings}
            onClose={onClose}
          />
        </>
      ) : (
        <div className="mt-6 flex shrink-0 items-center border-t border-border pt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-strong px-4 py-2 text-[length:var(--step--1)] font-medium text-foreground"
          >
            Close
          </button>
        </div>
      )}
    </Drawer>
  )
}
